/**
 * THE CALENDAR'S HALF OF THE INVITATION.
 *
 * The server has always emitted `meeting.updated` to every invitee's private
 * `user:` topic — `services/meetings.ts` does it at creation, `announceLifecycle`
 * does it on every reschedule and cancellation — and the gateway has always
 * subscribed every socket to `user:<self>` on handshake
 * (`apps/gateway/src/core/handshake.ts`). The frames arrived. Nothing in
 * `features/calendar` listened to any of them: a grep of that whole folder for
 * `realtime`, `subscribe`, `onEvent` or `topicFor` returned nothing.
 *
 * So an invitee sitting on the calendar page while somebody scheduled them saw
 * an empty grid until they navigated away and back, and an attendee looking at
 * a meeting that had just been cancelled saw it as still on. Both of those are
 * the CLIENT throwing away an event that was already on the wire — the whole
 * fix is this file plus one line in RealtimeRuntime.
 *
 * ── WHY INVALIDATE RATHER THAN PATCH ─────────────────────────────────────
 *
 * `applyToMeeting.ts` writes straight into the cache and says, correctly, that
 * nothing there refetches. The calendar cannot do that, and `lib/refresh.ts`
 * already explains why for the local writes: the grid's key carries the WINDOW
 * (`CALENDAR_KEYS.window(from, to)`), a moved meeting leaves one window and
 * enters another, and only the server knows which windows are wrong afterwards.
 * A `meeting.updated` carrying a new `scheduledStart` is exactly that case, so
 * this reducer does what every calendar mutation already does: invalidate the
 * `calendar` tree and let the open window refetch itself. Every other window is
 * marked stale and costs nothing until it is looked at again.
 *
 * ── WHY IT IS NOT GATED ON BEING ON THE CALENDAR PAGE ────────────────────
 *
 * Because `invalidateQueries` on a tree with no active observers is free — it
 * only stamps the entries stale. Wiring this to the page's mount instead would
 * mean a meeting cancelled while you were reading chat still showed as on when
 * you opened your diary, which is the same defect with a smaller window.
 */
import type { QueryClient } from '@tanstack/react-query';
import type { RealtimeEvent } from '@cyphex/types/realtime';

import { CALENDAR_KEYS } from '../api/queryKeys';

/**
 * The four frames that can change what a calendar should be showing.
 *
 * `meeting.updated` covers scheduling, rescheduling, an invite-list edit and
 * every per-occurrence change — `announceOccurrence` deliberately reuses it
 * rather than inventing a `meeting.occurrence.*` frame that would need a
 * gateway release before a single client could act on it.
 *
 * `meeting.started` and `meeting.ended` are here because the detail dialog
 * renders a Join button off `status`, and a button that says Join for a meeting
 * that ended twenty minutes ago is worse than no button.
 */
const CALENDAR_EVENTS: ReadonlySet<RealtimeEvent['type']> = new Set([
  'meeting.updated',
  'meeting.cancelled',
  'meeting.started',
  'meeting.ended',
]);

export function applyEventToCalendar(client: QueryClient, event: RealtimeEvent): void {
  if (!CALENDAR_EVENTS.has(event.type)) return;
  // Fire-and-forget on purpose: this is a reducer called from the socket's
  // event handler, and awaiting a refetch there would hold the frame loop for
  // the length of a network round trip.
  void client.invalidateQueries({ queryKey: CALENDAR_KEYS.all });
}
